import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, ArrowDown } from 'lucide-react';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { slidersAPI } from '../../services/api';

const AdminSliders = () => {
  const navigate = useNavigate();
  const [sliders, setSliders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchSliders();
  }, [navigate]);

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const response = await slidersAPI.getAll(false); // Get all sliders, including inactive
      if (response.records) {
        setSliders(response.records);
      }
    } catch (error) {
      console.error('Error fetching sliders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSlider(null);
    setShowModal(true);
  };

  const handleEdit = (slider: any) => {
    setEditingSlider(slider);
    setShowModal(true);
  };

  const handleDelete = async (slider: any) => {
    try {
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer le slider "${slider.title_fr}" ?`)) {
        await slidersAPI.delete(slider.id);
        setSliders(prev => prev.filter(item => item.id !== slider.id));
      }
    } catch (error) {
      console.error('Error deleting slider:', error);
      alert('Erreur lors de la suppression du slider');
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingSlider) {
        // Update existing slider
        const updatedData = { ...formData, id: editingSlider.id };
        await slidersAPI.update(updatedData);
        
        setSliders(prev => prev.map(item => 
          item.id === editingSlider.id ? { ...item, ...formData } : item
        ));
      } else {
        // Add new slider
        const response = await slidersAPI.create(formData);
        
        if (response.id) {
          const newSlider = {
            id: response.id,
            ...formData,
            created_at: new Date().toISOString()
          };
          setSliders(prev => [newSlider, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingSlider(null);
    } catch (error) {
      console.error('Error saving slider:', error);
      alert('Erreur lors de l\'enregistrement du slider');
    } finally {
      setFormLoading(false);
    }
  };

  const handleMoveUp = async (slider: any) => {
    try {
      const currentIndex = sliders.findIndex(item => item.id === slider.id);
      if (currentIndex <= 0) return; // Already at the top
      
      const newPosition = slider.order_position - 1;
      const updatedData = { ...slider, order_position: newPosition, id: slider.id };
      
      await slidersAPI.update(updatedData);
      
      // Update the slider that was above this one
      const aboveSlider = sliders[currentIndex - 1];
      const aboveUpdatedData = { ...aboveSlider, order_position: slider.order_position, id: aboveSlider.id };
      await slidersAPI.update(aboveUpdatedData);
      
      // Refresh the list
      fetchSliders();
    } catch (error) {
      console.error('Error moving slider up:', error);
      alert('Erreur lors du déplacement du slider');
    }
  };

  const handleMoveDown = async (slider: any) => {
    try {
      const currentIndex = sliders.findIndex(item => item.id === slider.id);
      if (currentIndex >= sliders.length - 1) return; // Already at the bottom
      
      const newPosition = slider.order_position + 1;
      const updatedData = { ...slider, order_position: newPosition, id: slider.id };
      
      await slidersAPI.update(updatedData);
      
      // Update the slider that was below this one
      const belowSlider = sliders[currentIndex + 1];
      const belowUpdatedData = { ...belowSlider, order_position: slider.order_position, id: belowSlider.id };
      await slidersAPI.update(belowUpdatedData);
      
      // Refresh the list
      fetchSliders();
    } catch (error) {
      console.error('Error moving slider down:', error);
      alert('Erreur lors du déplacement du slider');
    }
  };

  const formFields = [
    {
      name: 'title_fr',
      label: 'Titre (Français)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre du slider en français'
    },
    {
      name: 'title_en',
      label: 'Titre (Anglais)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre du slider en anglais'
    },
    {
      name: 'subtitle_fr',
      label: 'Sous-titre (Français)',
      type: 'textarea' as const,
      placeholder: 'Sous-titre du slider en français',
      rows: 2
    },
    {
      name: 'subtitle_en',
      label: 'Sous-titre (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Sous-titre du slider en anglais',
      rows: 2
    },
    {
      name: 'image_url',
      label: 'URL de l\'image',
      type: 'text' as const,
      required: true,
      placeholder: 'https://example.com/image.jpg'
    },
    {
      name: 'button_text_fr',
      label: 'Texte du bouton (Français)',
      type: 'text' as const,
      placeholder: 'Texte du bouton en français'
    },
    {
      name: 'button_text_en',
      label: 'Texte du bouton (Anglais)',
      type: 'text' as const,
      placeholder: 'Texte du bouton en anglais'
    },
    {
      name: 'button_url',
      label: 'URL du bouton',
      type: 'text' as const,
      placeholder: '/page-cible'
    },
    {
      name: 'order_position',
      label: 'Position',
      type: 'text' as const,
      placeholder: '1'
    },
    {
      name: 'active',
      label: 'Actif',
      type: 'checkbox' as const
    }
  ];

  const columns = [
    {
      key: 'order_position',
      label: 'Position',
      sortable: true,
      render: (value: number) => (
        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full font-bold">
          {value}
        </span>
      )
    },
    {
      key: 'title_fr',
      label: 'Titre',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          {row.image_url && (
            <img 
              src={row.image_url} 
              alt={value}
              className="w-16 h-10 object-cover rounded mr-3"
            />
          )}
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.title_en}</div>
          </div>
        </div>
      )
    },
    {
      key: 'button_text_fr',
      label: 'Bouton',
      render: (value: string, row: any) => (
        <div>
          <span className="text-sm font-medium">{value || '-'}</span>
          {row.button_url && (
            <div className="text-xs text-blue-600">{row.button_url}</div>
          )}
        </div>
      )
    },
    {
      key: 'active',
      label: 'Statut',
      render: (value: boolean) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Actif' : 'Inactif'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR')
    },
    {
      key: 'position',
      label: 'Ordre',
      render: (_: any, row: any) => (
        <div className="flex items-center space-x-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleMoveUp(row);
            }}
            className="p-1 rounded hover:bg-gray-100"
            title="Monter"
          >
            <ArrowUp className="h-4 w-4 text-blue-600" />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleMoveDown(row);
            }}
            className="p-1 rounded hover:bg-gray-100"
            title="Descendre"
          >
            <ArrowDown className="h-4 w-4 text-blue-600" />
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des sliders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Gestion des Sliders"
          data={sliders}
          columns={columns}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingSlider(null);
          }}
          title={editingSlider ? 'Modifier le slider' : 'Nouveau slider'}
          size="lg"
        >
          <AdminForm
            fields={formFields}
            initialData={editingSlider || { active: true, order_position: sliders.length + 1 }}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowModal(false);
              setEditingSlider(null);
            }}
            loading={formLoading}
          />
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminSliders;